import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Edit, Delete, Close, Badge, Paid, ForwardToInbox, Schedule, Gavel } from "@mui/icons-material";
import ContractStandingChip from "../components/ContractStandingChip";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import StandardPagination from "../components/Pagination/StandardPagination";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import PasswordInviteChannelModal, { formatWhatsApp } from "../components/modals/PasswordInviteChannelModal";
import type { PasswordInviteOutcome } from "../services/entityUserService";
import { Toast } from "../components/Toast";
import ConfirmModal from "../components/modals/ConfirmModal";
import EntityUserProfilesModal from "../components/modals/EntityUserProfilesModal";
import SavingOverlay from "../components/SavingOverlay";
import {
  entityUserService,
  EntityUser,
  EntityUserFormOptions,
} from "../services/entityUserService";
import { colors, typography, inputs, pageHeader } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import ResultCount from "../components/ResultCount";
import { maskDocument, isValidCpf, isValidCnpj } from "../utils/document";
import PhoneWithCountryInput from "../components/PhoneWithCountryInput";
import { EMPTY_PHONE, PhoneValue, legacyPhonePart, phoneValueFromParts } from "../utils/phone";

interface UserForm {
  ueprId: string;
  email: string;
  fullName: string;
  userNickName: string;
  document: string;
  documentProfessional: string;
  cnpj: string;
  razaoSocial: string;
  inscricaoMunicipal: string;
  professionalTypeId: string;
  phone: PhoneValue;
  addressZipCode: string;
  addressStreet: string;
  addressNumber: string;
  addressComplement: string;
  addressDistrict: string;
  addressCity: string;
  addressState: string;
  bankCode: string;
  bankAgency: string;
  bankAccount: string;
  bankAccountDigit: string;
  bankPix: string;
  active: boolean;
}

const emptyForm: UserForm = {
  ueprId: "",
  email: "",
  fullName: "",
  userNickName: "",
  document: "",
  documentProfessional: "",
  cnpj: "",
  razaoSocial: "",
  inscricaoMunicipal: "",
  professionalTypeId: "",
  phone: EMPTY_PHONE,
  addressZipCode: "",
  addressStreet: "",
  addressNumber: "",
  addressComplement: "",
  addressDistrict: "",
  addressCity: "",
  addressState: "",
  bankCode: "",
  bankAgency: "",
  bankAccount: "",
  bankAccountDigit: "",
  bankPix: "",
  active: true,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EntityUsers: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName = params.get("entityName") || user?.entityName || "Entidade";
  const canServiceValues = (user?.allowedPages || []).includes(
    "professional-service-values"
  );
  const canAvailability = (user?.allowedPages || []).includes(
    "professional-availability"
  );
  // Contratos do profissional (item 236): ícone da linha + indicativo vigente/pendente (RN-558e)
  const canContracts = (user?.allowedPages || []).includes("professional-contracts");
  const openContracts = (u: EntityUser) =>
    navigateTo("professional-contracts", {
      professionalId: u.ueprId,
      professionalName: u.name || u.email || "",
      especialidade: u.professionalTypeName || "",
      entityId,
      entityName,
    });

  const [users, setUsers] = useState<EntityUser[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"todos" | "ativo" | "inativo">("todos");
  const [typeFilter, setTypeFilter] = useState("");
  const [profileFilter, setProfileFilter] = useState("");
  type SortField = "name" | "email" | "professionalTypeName" | "profileName" | "active";
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortOrder === "asc" ? " ↑" : " ↓") : "";
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);

  // busca por nome sem sensibilidade a acentos/caixa
  const normalize = (v: string) =>
    v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filteredUsers = users
    .filter((u) => {
      const matchesName =
        !searchTerm.trim() || normalize(u.name || "").includes(normalize(searchTerm));
      const matchesStatus =
        statusFilter === "todos" || (statusFilter === "ativo" ? u.active : !u.active);
      const matchesType = !typeFilter || u.professionalTypeName === typeFilter;
      const matchesProfile = !profileFilter || u.profileName === profileFilter;
      return matchesName && matchesStatus && matchesType && matchesProfile;
    })
    .sort((a, b) => {
      const compareValue =
        sortField === "active"
          ? Number(a.active) - Number(b.active)
          : normalize(a[sortField] || "").localeCompare(normalize(b[sortField] || ""));
      return sortOrder === "asc" ? compareValue : -compareValue;
    });
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const [options, setOptions] = useState<EntityUserFormOptions>({
    professionalTypes: [],
    profiles: [],
    banks: [],
    teams: [],
  });

  // Modal 1 (email gate)
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [emailError, setEmailError] = useState("");
  const [provisioning, setProvisioning] = useState(false);

  // Modal 2 (dados completos)
  const [dataModalOpen, setDataModalOpen] = useState(false);
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [nameError, setNameError] = useState("");
  const [cpfError, setCpfError] = useState("");
  const [cnpjError, setCnpjError] = useState("");
  const [saving, setSaving] = useState(false);

  const [toInactivate, setToInactivate] = useState<EntityUser | null>(null);
  const [profilesModalUser, setProfilesModalUser] = useState<EntityUser | null>(
    null
  );

  const loadUsers = async () => {
    if (!entityId) return;
    try {
      const data = await entityUserService.getByEntity(entityId);
      setUsers(data);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
      showToast("Erro ao carregar os usuários.", "error");
    }
  };

  const loadOptions = async () => {
    if (!entityId) return;
    try {
      const data = await entityUserService.getFormOptions(entityId);
      setOptions(data);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
    }
  };

  useEffect(() => {
    document.title = `${entityName} / Usuários`;
    loadUsers();
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Abre o modal de email (passo 1 do cadastro de usuário novo).
  const openEmailGate = () => {
    setEmail("");
    setEmailConfirm("");
    setEmailError("");
    setEmailModalOpen(true);
  };

  const handleProvision = async () => {
    if (provisioning) return;
    const normalized = email.trim();
    if (!EMAIL_REGEX.test(normalized)) {
      setEmailError("Email inválido");
      return;
    }
    if (normalized.toLowerCase() !== emailConfirm.trim().toLowerCase()) {
      setEmailError("A confirmação não confere");
      return;
    }
    setProvisioning(true);
    try {
      const result = await entityUserService.provision(entityId, normalized);
      setEmailModalOpen(false);
      // Conta nova nasce com nome = email (placeholder); deixa em branco para preenchimento.
      setForm({
        ...emptyForm,
        ueprId: result.ueprId,
        email: result.email,
        fullName: result.isNewUser ? "" : result.fullName,
      });
      setNameError("");
      setDataModalOpen(true);
      // Conta nova recebe convite de senha (RN-005b). Sem email (homolog), o backend
      // devolve o link para a equipe repassar manualmente.
      if (result.isNewUser) {
        if (result.inviteLink) {
          await navigator.clipboard?.writeText(result.inviteLink).catch(() => undefined);
          showToast("Convite de senha gerado. Link copiado para a área de transferência.", "success");
        } else {
          showToast("Convite de senha enviado por email.", "success");
        }
      }
      await loadUsers();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao processar o email.";
      showToast(message, "error");
    } finally {
      setProvisioning(false);
    }
  };

  // Edição de um vínculo existente: abre direto o modal de dados.
  const openEdit = (u: EntityUser) => {
    setForm({
      ueprId: u.ueprId,
      email: u.email || "",
      fullName: u.name || "",
      userNickName: u.userNickName || "",
      document: u.document || "",
      documentProfessional: u.documentProfessional || "",
      cnpj: u.cnpj || "",
      razaoSocial: u.razaoSocial || "",
      inscricaoMunicipal: u.inscricaoMunicipal || "",
      professionalTypeId: u.professionalTypeId || "",
      phone: phoneValueFromParts(u.phoneDdi, legacyPhonePart(u.phoneCode), legacyPhonePart(u.phoneNumber)),
      addressZipCode: u.addressZipCode || "",
      addressStreet: u.addressStreet || "",
      addressNumber: u.addressNumber || "",
      addressComplement: u.addressComplement || "",
      addressDistrict: u.addressDistrict || "",
      addressCity: u.addressCity || "",
      addressState: u.addressState || "",
      bankCode: u.bankCode || "",
      bankAgency: u.bankAgency || "",
      bankAccount: u.bankAccount || "",
      bankAccountDigit: u.bankAccountDigit || "",
      bankPix: u.bankPix || "",
      active: u.active,
    });
    setNameError("");
    setCpfError("");
    setCnpjError("");
    setDataModalOpen(true);
  };

  // ViaCEP: ao informar um CEP válido (8 dígitos), preenche logradouro, bairro, cidade e UF.
  const fillAddressByCep = async (rawCep: string) => {
    const cep = rawCep.replace(/\D/g, "");
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      if (!response.ok) return;
      const data = await response.json();
      if (data?.erro) return;
      setForm((f) => ({
        ...f,
        addressStreet: data.logradouro || f.addressStreet,
        addressDistrict: data.bairro || f.addressDistrict,
        addressCity: data.localidade || f.addressCity,
        addressState: data.uf || f.addressState,
      }));
    } catch {
      // falha de rede — mantém os campos para preenchimento manual
    }
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.fullName.trim()) {
      setNameError("Nome obrigatório");
      return;
    }

    // CPF/CNPJ: validados pelos dígitos verificadores apenas quando preenchidos.
    const cpfDigits = form.document.replace(/\D/g, "");
    if (cpfDigits && !isValidCpf(cpfDigits)) {
      setCpfError("CPF inválido");
      return;
    }
    setCpfError("");

    const cnpjDigits = form.cnpj.replace(/\D/g, "");
    if (cnpjDigits && !isValidCnpj(cnpjDigits)) {
      setCnpjError("CNPJ inválido");
      return;
    }
    setCnpjError("");

    setSaving(true);
    try {
      await entityUserService.update(form.ueprId, {
        fullName: form.fullName.trim(),
        userNickName: form.userNickName.trim() || undefined,
        document: form.document.trim() || undefined,
        documentProfessional: form.documentProfessional.trim() || undefined,
        cnpj: form.cnpj.trim() || undefined,
        razaoSocial: form.razaoSocial.trim() || undefined,
        inscricaoMunicipal: form.inscricaoMunicipal.trim() || undefined,
        professionalTypeId: form.professionalTypeId || undefined,
        phoneCode: form.phone.ddd || undefined,
        phoneNumber: form.phone.number || undefined,
        phoneDdi: form.phone.ddi,
        addressZipCode: form.addressZipCode.trim() || undefined,
        addressStreet: form.addressStreet.trim() || undefined,
        addressNumber: form.addressNumber.trim() || undefined,
        addressComplement: form.addressComplement.trim() || undefined,
        addressDistrict: form.addressDistrict.trim() || undefined,
        addressCity: form.addressCity.trim() || undefined,
        addressState: form.addressState.trim() || undefined,
        bankCode: form.bankCode.trim() || undefined,
        bankAgency: form.bankAgency.trim() || undefined,
        bankAccount: form.bankAccount.trim() || undefined,
        bankAccountDigit: form.bankAccountDigit.trim() || undefined,
        bankPix: form.bankPix.trim() || undefined,
        active: form.active,
      });
      showToast("Usuário salvo com sucesso", "success");
      setDataModalOpen(false);
      await loadUsers();
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);
      showToast("Erro ao salvar o usuário. Tente novamente.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (u: EntityUser) => {
    try {
      await entityUserService.setStatus(u.ueprId, !u.active);
      showToast(!u.active ? "Usuário ativado" : "Usuário inativado", "success");
      await loadUsers();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      showToast("Erro ao alterar o status do usuário.", "error");
    }
  };

  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [inviteTarget, setInviteTarget] = useState<EntityUser | null>(null);
  const [inviteFailed, setInviteFailed] = useState<PasswordInviteOutcome | null>(null);
  const [inviteLinkOnly, setInviteLinkOnly] = useState(false);

  // O ícone abre a escolha do canal (RN-005d); o envio em si acontece no handleSendInvite.
  const handleResendInvite = (u: EntityUser) => {
    if (!u.userId) return;
    setInviteFailed(null);
    setInviteLinkOnly(false);
    setInviteTarget(u);
  };

  const handleSendInvite = async (channels: { sendEmail: boolean; sendWhatsApp: boolean }) => {
    const u = inviteTarget;
    if (!u?.userId) return;
    setResendingUserId(u.userId);
    try {
      const outcome = await entityUserService.resendPasswordInvite(u.userId, entityId, channels);
      // "Gerar link": sem canal, o link volta para o administrador copiar e enviar
      const linkOnly = !channels.sendEmail && !channels.sendWhatsApp;
      setInviteLinkOnly(linkOnly);

      const saiu: string[] = [];
      if (outcome.emailSent) saiu.push("email");
      if (outcome.whatsappSent) saiu.push("WhatsApp");

      if (saiu.length > 0) {
        setInviteTarget(null);
        const aviso = outcome.whatsappError ? ` WhatsApp não saiu: ${outcome.whatsappError}` : "";
        showToast(`Convite enviado por ${saiu.join(" e ")}.${aviso}`, outcome.whatsappError ? "error" : "success");
      } else {
        // Nenhum canal entregou (homolog, provedor fora, número errado): o modal fica aberto com o
        // link para copiar e mandar por outro meio.
        setInviteFailed(outcome);
      }
    } catch (error) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Não foi possível enviar o convite.";
      showToast(message, "error");
    } finally {
      setResendingUserId(null);
    }
  };

  const handleConfirmInactivate = async () => {
    if (!toInactivate) return;
    try {
      await entityUserService.setStatus(toInactivate.ueprId, false);
      showToast("Usuário inativado", "success");
      setToInactivate(null);
      await loadUsers();
    } catch (error) {
      console.error("Erro ao inativar usuário:", error);
      showToast("Erro ao inativar o usuário.", "error");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters className="c4u-page">
          <div className="c4u-page-header c4u-page-header--in-panel">
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Usuários</h1>
              <p className="c4u-page-header__subtitle">
                Profissionais e usuários vinculados a esta entidade, com tipo profissional e
                perfil de acesso.
              </p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
            <div className="c4u-filters__fields">
              <TextField
                label="Busca por nome"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Buscar"
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 120 }}
                sx={{
                  ...searchFieldSx,
                  "& .MuiOutlinedInput-root": {
                    height: inputs.default.height,
                    "& fieldset": {
                      borderColor: colors.border,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.border,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: inputs.default.labelFontSize,
                    color: colors.textSecondary,
                    backgroundColor: colors.white,
                    padding: inputs.default.labelPadding,
                    "&.Mui-focused": {
                      color: colors.primary,
                    },
                  },
                }}
              />

              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as "todos" | "ativo" | "inativo");
                  setCurrentPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  ...filterFieldSx,
                  "& .MuiOutlinedInput-root": {
                    height: inputs.default.height,
                    backgroundColor: colors.white,
                    "& fieldset": {
                      borderColor: colors.border,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.border,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: inputs.default.labelFontSize,
                    color: colors.textSecondary,
                    backgroundColor: colors.white,
                    padding: inputs.default.labelPadding,
                    "&.Mui-focused": {
                      color: colors.primary,
                    },
                  },
                }}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
              </TextField>

              <TextField
                select
                label="Tipo profissional"
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...filterFieldSx, "& .MuiOutlinedInput-root": { height: inputs.default.height } }}
              >
                <MenuItem value="">Todos</MenuItem>
                {options.professionalTypes.map((o) => (
                  <MenuItem key={o.id} value={o.name}>{o.name}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Perfil"
                value={profileFilter}
                onChange={(e) => { setProfileFilter(e.target.value); setCurrentPage(1); }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...filterFieldSx, "& .MuiOutlinedInput-root": { height: inputs.default.height } }}
              >
                <MenuItem value="">Todos</MenuItem>
                {options.profiles.map((o) => (
                  <MenuItem key={o.id} value={o.name}>{o.name}</MenuItem>
                ))}
              </TextField>

              {/* Ações da barra: limpar e incluir, no fim (padrão do design system) */}
            </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton
                  disabled={!(searchTerm || statusFilter !== "todos" || typeFilter || profileFilter)}
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("todos");
                      setTypeFilter("");
                      setProfileFilter("");
                      setSortField("name");
                      setSortOrder("asc");
                      setCurrentPage(1);
                  }}
                />
                <AddButton onClick={openEmailGate} title="Adicionar usuário" />
              </div>
            </div>

          <ResultCount shown={filteredUsers.length} total={users.length} />

          <Box
            className="admin-plans-list-container"
            sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}
          >
            <Box className="admin-plans-table admin-entities-table">
              <Box className="c4u-table__header">
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 280px", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("name")}
                  title="Ordenar por nome"
                >
                  Nome{sortIndicator("name")}
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--xl"
                  sx={{ flex: "0 0 260px", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("email")}
                  title="Ordenar por email"
                >
                  Email{sortIndicator("email")}
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--lg"
                  sx={{ flex: "0 0 150px", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("professionalTypeName")}
                  title="Ordenar por tipo profissional"
                >
                  Tipo profissional{sortIndicator("professionalTypeName")}
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--md"
                  sx={{ flex: "1 1 auto", minWidth: 150, textAlign: "left", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("profileName")}
                  title="Ordenar por perfil"
                >
                  Perfil{sortIndicator("profileName")}
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 90px", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("active")}
                  title="Ordenar por status"
                >
                  Status{sortIndicator("active")}
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 232px", justifyContent: "flex-end" }}
                >
                  Ações
                </Box>
              </Box>

              <Box className="admin-plans-table-body">
                {paginatedUsers.map((u) => (
                  <Box
                    key={u.ueprId}
                    className="c4u-table__row"
                  >
                    <Box
                      className="admin-plans-cell"
                      sx={{
                        flex: "0 0 280px",
                        textAlign: "left",
                        fontWeight: 600,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}
                      data-label="Nome"
                      title={u.isProtected ? "Usuário do sistema: não é editado nem excluído; ativar/inativar pela tag de status" : u.name || undefined}
                    >
                      {u.name || "-"}
                      {u.isProtected && (
                        <Chip label="Sistema" size="small" sx={{ ml: 1, height: 20, fontSize: "0.7rem", fontWeight: 600, backgroundColor: "#e9ecef", color: "#495057" }} />
                      )}
                      {canContracts && !u.isProtected && (
                        <ContractStandingChip standing={u.contract} onClick={() => openContracts(u)} />
                      )}
                    </Box>
                    <Box
                      className="admin-plans-cell c4u-col--xl"
                      sx={{
                        flex: "0 0 260px",
                        textAlign: "left",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}
                      data-label="Email"
                      title={u.email || undefined}
                    >
                      {u.email || "-"}
                    </Box>
                    <Box
                      className="admin-plans-cell c4u-col--lg"
                      sx={{ flex: "0 0 150px", textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      data-label="Tipo profissional"
                      title={u.professionalTypeName || undefined}
                    >
                      {u.professionalTypeName || "-"}
                    </Box>
                    <Box
                      className="admin-plans-cell c4u-col--md"
                      sx={{
                        flex: "1 1 auto",
                        minWidth: 140,
                        textAlign: "left",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}
                      data-label="Perfil"
                      title={u.profileName || undefined}
                    >
                      {u.profileName || "-"}
                    </Box>
                    <Box
                      className="admin-plans-cell"
                      sx={{ flex: "0 0 90px", textAlign: "left" }}
                      data-label="Status"
                    >
                      <Chip
                        label={u.active ? "Ativo" : "Inativo"}
                        size="small"
                        onClick={() => handleToggleStatus(u)}
                        title={u.active ? "Clique para inativar" : "Clique para ativar"}
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          cursor: "pointer",
                          backgroundColor: u.active ? "#28a745" : "#adb5bd",
                        }}
                      />
                    </Box>
                    <Box
                      className="admin-plans-cell admin-plans-actions"
                      sx={{ flex: "0 0 232px", textAlign: "right" }}
                      data-label="Ações"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "flex-end",
                          flexShrink: 0,
                        }}
                      >
                        <IconButton
                          className="c4u-row-action"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProfilesModalUser(u);
                          }}
                          disabled={u.isProtected}
                          title={u.isProtected ? "Usuário do sistema: ações bloqueadas (ativar/inativar pela tag de status)" : "Perfis do usuário"}
                          sx={{
                            backgroundColor: "#6f42c1",
                            color: "white",
                            "&:hover": { backgroundColor: "#5a32a3" },
                            "&.Mui-disabled": { backgroundColor: "#c9b8e6", color: "white" },
                          }}
                        >
                          <Badge sx={{ fontSize: "1rem" }} />
                        </IconButton>
                        {canServiceValues && u.professionalTypeId && (
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateTo("professional-service-values", {
                                professionalId: u.ueprId,
                                professionalName: u.name || u.email || "",
                                especialidade: u.professionalTypeName || "",
                                professionalTypeId: u.professionalTypeId || "",
                                entityId,
                                entityName,
                              });
                            }}
                            title="Valores de serviço"
                            sx={{
                              backgroundColor: "#fd7e14",
                              color: "white",
                              "&:hover": { backgroundColor: "#e8590c" },
                            }}
                          >
                            <Paid sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        )}
                        {canAvailability && u.professionalTypeId && (
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateTo("professional-availability", {
                                professionalId: u.ueprId,
                                professionalName: u.name || u.email || "",
                                especialidade: u.professionalTypeName || "",
                                entityId,
                                entityName,
                              });
                            }}
                            title="Disponibilidade"
                            sx={{
                              backgroundColor: "#0d6efd",
                              color: "white",
                              "&:hover": { backgroundColor: "#0b5ed7" },
                            }}
                          >
                            <Schedule sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        )}
                        {canContracts && !u.isProtected && (
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openContracts(u);
                            }}
                            title="Contratos do profissional"
                            sx={{
                              backgroundColor: "#198754",
                              color: "white",
                              "&:hover": { backgroundColor: "#157347" },
                            }}
                          >
                            <Gavel sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        )}
                        <IconButton
                          className="c4u-row-action"
                          size="small"
                          onClick={() => openEdit(u)}
                          disabled={u.isProtected}
                          title={u.isProtected ? "Usuário do sistema: ações bloqueadas (ativar/inativar pela tag de status)" : "Editar"}
                          sx={{
                            backgroundColor: "#03B4C6",
                            color: "white",
                            "&:hover": { backgroundColor: "#029AAB" },
                            "&.Mui-disabled": { backgroundColor: "#9adbe3", color: "white" },
                          }}
                        >
                          <Edit sx={{ fontSize: "1rem" }} />
                        </IconButton>
                        {u.userId && (
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              // Sem isto a linha abre a edicao por cima do modal de convite.
                              e.stopPropagation();
                              handleResendInvite(u);
                            }}
                            disabled={u.isProtected || !u.active || resendingUserId === u.userId}
                            title={u.isProtected ? "Usuário do sistema: ações bloqueadas (ativar/inativar pela tag de status)" : "Enviar convite de senha (email ou WhatsApp)"}
                            sx={{
                              backgroundColor: "#20a8a8",
                              color: "white",
                              "&:hover": { backgroundColor: "#1a8a8a" },
                              "&.Mui-disabled": {
                                backgroundColor: "#a8d5d5",
                                color: "white",
                              },
                            }}
                          >
                            <ForwardToInbox sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        )}
                        <IconButton
                          className="c4u-row-action"
                          size="small"
                          onClick={() => setToInactivate(u)}
                          disabled={u.isProtected || !u.active}
                          title={u.isProtected ? "Usuário do sistema: ações bloqueadas (ativar/inativar pela tag de status)" : u.active ? "Inativar" : "Já inativo"}
                          sx={{
                            backgroundColor: "#dc3545",
                            color: "white",
                            "&:hover": { backgroundColor: "#c82333" },
                            "&.Mui-disabled": {
                              backgroundColor: "#e9a3aa",
                              color: "white",
                            },
                          }}
                        >
                          <Delete sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {filteredUsers.length > itemsPerPage && (
            <Box sx={{ mt: 2 }}>
              <StandardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filteredUsers.length}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
              />
            </Box>
          )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      {/* Modal 1 — Email gate */}
      <Dialog
        open={emailModalOpen}
        onClose={(_, reason) => {
          if (provisioning) return;
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            setEmailModalOpen(false);
            return;
          }
          setEmailModalOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <SavingOverlay open={provisioning} />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Adicionar Usuário
          <IconButton
            onClick={() => setEmailModalOpen(false)}
            disabled={provisioning}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          <Typography variant="body2" sx={{ mb: 2, color: colors.textSecondary }}>
            Informe o email do usuário. Se já existir, ele será vinculado a esta
            entidade; caso contrário, uma conta será criada.
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            error={!!emailError}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirmar email"
            type="email"
            value={emailConfirm}
            onChange={(e) => {
              setEmailConfirm(e.target.value);
              if (emailError) setEmailError("");
            }}
            error={!!emailError}
            helperText={emailError}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            onPaste={(e) => e.preventDefault()}
          />
        </DialogContent>
        <DialogActions sx={{ p: "1rem 1.5rem", gap: 1 }}>
          <Button
            onClick={() => setEmailModalOpen(false)}
            disabled={provisioning}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleProvision}
            disabled={provisioning}
            variant="contained"
            startIcon={
              provisioning ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : undefined
            }
            sx={{
              backgroundColor: colors.primary,
              textTransform: "none",
              "&.Mui-disabled": {
                backgroundColor: colors.primary,
                opacity: 0.7,
                color: "white",
              },
            }}
          >
            {provisioning ? "Processando..." : "Continuar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 2 — Dados completos */}
      <Dialog
        open={dataModalOpen}
        onClose={(_, reason) => {
          if (saving) return;
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            setDataModalOpen(false);
            return;
          }
          setDataModalOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "92vh",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <SavingOverlay open={saving} />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Dados do Usuário
          <IconButton
            onClick={() => setDataModalOpen(false)}
            disabled={saving}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 2 }}>
            <TextField
              label="Nome"
              value={form.fullName}
              onChange={(e) => {
                setForm((f) => ({ ...f, fullName: e.target.value }));
                if (nameError) setNameError("");
              }}
              error={!!nameError}
              helperText={nameError}
              inputProps={{ maxLength: 100 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Apelido"
              value={form.userNickName}
              onChange={(e) =>
                setForm((f) => ({ ...f, userNickName: e.target.value }))
              }
              inputProps={{ maxLength: 50 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 2 }}>
            <TextField
              label="CPF"
              value={form.document}
              onChange={(e) => {
                setForm((f) => ({
                  ...f,
                  document: maskDocument(e.target.value, "fisica"),
                }));
                if (cpfError) setCpfError("");
              }}
              error={!!cpfError}
              helperText={cpfError}
              inputProps={{ maxLength: 14 }}
              sx={{ width: { xs: "100%", sm: 180 }, flexShrink: 0 }}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Tipo profissional"
              value={form.professionalTypeId}
              onChange={(e) =>
                setForm((f) => ({ ...f, professionalTypeId: e.target.value }))
              }
              sx={{ minWidth: { xs: "100%", sm: 260 }, flexShrink: 0 }}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {options.professionalTypes.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Documento profissional"
              value={form.documentProfessional}
              onChange={(e) =>
                setForm((f) => ({ ...f, documentProfessional: e.target.value }))
              }
              placeholder="Registro no conselho (CRP, CRM, etc.)"
              inputProps={{ maxLength: 30 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <TextField
              label="Email"
              value={form.email}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
            />
            <PhoneWithCountryInput
              label="Telefone"
              splitDdd
              dddWidth={{ xs: 64, sm: 100 }}
              value={form.phone}
              onChange={(phone) => setForm((f) => ({ ...f, phone }))}
              size="medium"
              margin="dense"
              fieldSx={{}}
              labelProps={{ shrink: true }}
              gap={{ xs: 1, sm: 2 }}
            />
          </Box>

          <Typography
            variant="subtitle2"
            sx={{ mt: 2, mb: 0.5, color: colors.textSecondary }}
          >
            Dados Comerciais
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 2 }}>
            <TextField
              label="CNPJ"
              value={form.cnpj}
              onChange={(e) => {
                setForm((f) => ({
                  ...f,
                  cnpj: maskDocument(e.target.value, "juridica"),
                }));
                if (cnpjError) setCnpjError("");
              }}
              error={!!cnpjError}
              helperText={cnpjError}
              inputProps={{ maxLength: 18 }}
              sx={{ width: { xs: "100%", sm: 200 }, flexShrink: 0 }}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Razão Social"
              value={form.razaoSocial}
              onChange={(e) =>
                setForm((f) => ({ ...f, razaoSocial: e.target.value }))
              }
              inputProps={{ maxLength: 100 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Inscrição municipal"
              value={form.inscricaoMunicipal}
              onChange={(e) =>
                setForm((f) => ({ ...f, inscricaoMunicipal: e.target.value }))
              }
              inputProps={{ maxLength: 20 }}
              sx={{ width: { xs: "100%", sm: 180 }, flexShrink: 0 }}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="CEP"
            value={form.addressZipCode}
            onChange={(e) => {
              const cep = e.target.value.replace(/\D/g, "").slice(0, 8);
              setForm((f) => ({ ...f, addressZipCode: cep }));
              if (cep.length === 8) fillAddressByCep(cep);
            }}
            inputProps={{ inputMode: "numeric", maxLength: 8 }}
            sx={{ width: { xs: "100%", sm: 140 }, mb: 2 }}
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 2 }}>
            <TextField
              label="Logradouro"
              value={form.addressStreet}
              onChange={(e) =>
                setForm((f) => ({ ...f, addressStreet: e.target.value }))
              }
              inputProps={{ maxLength: 100 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Número"
              value={form.addressNumber}
              onChange={(e) =>
                setForm((f) => ({ ...f, addressNumber: e.target.value }))
              }
              inputProps={{ maxLength: 10 }}
              sx={{ width: { xs: "100%", sm: 110 }, flexShrink: 0 }}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Complemento"
              value={form.addressComplement}
              onChange={(e) =>
                setForm((f) => ({ ...f, addressComplement: e.target.value }))
              }
              inputProps={{ maxLength: 60 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 2 }}>
            <TextField
              label="Bairro"
              value={form.addressDistrict}
              onChange={(e) =>
                setForm((f) => ({ ...f, addressDistrict: e.target.value }))
              }
              inputProps={{ maxLength: 60 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Cidade"
              value={form.addressCity}
              onChange={(e) =>
                setForm((f) => ({ ...f, addressCity: e.target.value }))
              }
              inputProps={{ maxLength: 60 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="UF"
              value={form.addressState}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  addressState: e.target.value.toUpperCase().slice(0, 2),
                }))
              }
              inputProps={{ maxLength: 2 }}
              sx={{ width: { xs: "100%", sm: 90 }, flexShrink: 0 }}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Typography
            variant="subtitle2"
            sx={{ mt: 2, mb: 0.5, color: colors.textSecondary }}
          >
            Dados bancários
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Autocomplete
              options={options.banks}
              getOptionLabel={(o) => `${o.code} - ${o.name}`}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              value={options.banks.find((b) => b.code === form.bankCode) || null}
              onChange={(_, v) =>
                setForm((f) => ({ ...f, bankCode: v?.code || "" }))
              }
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Banco"
                  placeholder="Buscar por código ou nome"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 2 }}>
            <TextField
              label="Agência"
              value={form.bankAgency}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  bankAgency: e.target.value.replace(/\D/g, ""),
                }))
              }
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
              sx={{ width: { xs: "100%", sm: 130 }, flexShrink: 0 }}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Conta"
              value={form.bankAccount}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  bankAccount: e.target.value.replace(/\D/g, ""),
                }))
              }
              inputProps={{ inputMode: "numeric", maxLength: 15 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Dígito"
              value={form.bankAccountDigit}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  bankAccountDigit: e.target.value
                    .replace(/[^0-9Xx]/g, "")
                    .toUpperCase()
                    .slice(0, 2),
                }))
              }
              inputProps={{ inputMode: "numeric", maxLength: 2 }}
              sx={{ width: { xs: "100%", sm: 110 }, flexShrink: 0 }}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="PIX"
              value={form.bankPix}
              onChange={(e) =>
                setForm((f) => ({ ...f, bankPix: e.target.value }))
              }
              inputProps={{ maxLength: 77 }}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: "1rem 1.5rem", gap: 1 }}>
          <Button
            onClick={() => setDataModalOpen(false)}
            disabled={saving}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : undefined
            }
            sx={{
              backgroundColor: colors.primary,
              textTransform: "none",
              "&.Mui-disabled": {
                backgroundColor: colors.primary,
                opacity: 0.7,
                color: "white",
              },
            }}
          >
            {saving ? "Processando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!toInactivate}
        title="Inativar Usuário"
        message={
          toInactivate
            ? `Tem certeza que deseja inativar o usuário "${
                toInactivate.name || toInactivate.email
              }" nesta entidade?`
            : ""
        }
        warningMessage="O usuário perde o acesso a esta entidade. É possível reativar depois."
        confirmButtonText="Inativar"
        cancelButtonText="Cancelar"
        onConfirm={handleConfirmInactivate}
        onCancel={() => setToInactivate(null)}
        type="danger"
      />

      {profilesModalUser &&
        (() => {
          const adminProfileIds = options.profiles
            .filter((p) => p.isAdmin)
            .map((p) => p.id);
          const hasAdmin = (u: EntityUser) =>
            (u.profileIds || []).some((id) => adminProfileIds.includes(id));
          const activeAdminHolders = users.filter(
            (u) => u.active && hasAdmin(u)
          ).length;
          const lockAdminUncheck =
            profilesModalUser.active &&
            hasAdmin(profilesModalUser) &&
            activeAdminHolders <= 1;
          return (
            <EntityUserProfilesModal
              isOpen={true}
              onClose={() => setProfilesModalUser(null)}
              ueprId={profilesModalUser.ueprId}
              userName={profilesModalUser.name || profilesModalUser.email || ""}
              profiles={options.profiles}
              initialProfileIds={profilesModalUser.profileIds || []}
              teams={options.teams}
              initialTeamIds={profilesModalUser.teamIds || []}
              onSaved={loadUsers}
              lockAdminUncheck={lockAdminUncheck}
            />
          );
        })()}

      <PasswordInviteChannelModal
        key={inviteTarget?.userId ?? "none"}
        open={inviteTarget !== null}
        userName={inviteTarget?.name || inviteTarget?.email || "o usuário"}
        email={inviteTarget?.email || null}
        whatsapp={formatWhatsApp(inviteTarget?.phoneDdi, inviteTarget?.phoneCode, inviteTarget?.phoneNumber)}
        sending={resendingUserId !== null}
        failedOutcome={inviteFailed}
        whatsappAvailable={Boolean(options?.hasWhatsAppChannel)}
        allowLinkOnly
        linkGenerated={inviteLinkOnly}
        onClose={() => {
          setInviteTarget(null);
          setInviteFailed(null);
          setInviteLinkOnly(false);
        }}
        onSend={handleSendInvite}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default EntityUsers;
